import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import PageHero from '@/components/base/PageHero';

export default function PrivacyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy — The Great Multitude Church International';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <PageHero
          title="Privacy Policy"
          subtitle="How we collect, use, and protect your personal information."
          breadcrumb="Privacy Policy"
          slot="privacy_hero"
        />

        <section className="py-16 md:py-24">
          <div className="max-w-[820px] mx-auto px-4 md:px-8">
            <p className="text-foreground-500 text-sm mb-10">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="space-y-10 text-foreground-700 leading-relaxed text-base">
              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">1. Introduction</h2>
                <p>
                  The Great Multitude Church International (&ldquo;TGMCI,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;)
                  is committed to protecting your privacy. We are a ministry dedicated to empowering youth through faith and
                  community, with our head office in Dansoman, Accra, Ghana, and a branch in Edea, Cameroon. This Privacy
                  Policy explains how we collect, use, disclose, and safeguard your information when you visit our website,
                  submit a prayer request, subscribe to our newsletter, share your testimony, or make a donation inquiry
                  through the PureHeart Africa program.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">2. Information We Collect</h2>
                <p className="mb-3">We may collect the following types of information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Personal Information:</strong> your name, email address, phone number, and location when you contact us, subscribe to our newsletter, submit a prayer request, or share a testimony.</li>
                  <li><strong>Donation Information:</strong> the amount and payment method you indicate when making a donation inquiry to PureHeart Africa (we do not store full payment card details).</li>
                  <li><strong>Usage Data:</strong> anonymous technical information such as your browser type, device, and pages visited, used to improve our website.</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">3. How We Use Your Information</h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To respond to your inquiries, prayer requests, and messages.</li>
                  <li>To send you newsletters, announcements, and ministry updates (you may unsubscribe at any time).</li>
                  <li>To process and acknowledge donation inquiries.</li>
                  <li>To improve our website, services, and outreach programs.</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">4. Sharing Your Information</h2>
                <p>
                  We do not sell, rent, or trade your personal information. We may share your information only with trusted
                  service providers who help us operate our website and process communications, and only to the extent
                  necessary to provide those services. We may also disclose information when required by law.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">5. Data Security</h2>
                <p>
                  We use appropriate technical and organizational measures to protect your personal information against
                  unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the
                  internet is completely secure, and we cannot guarantee absolute security.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">6. Cookies</h2>
                <p>
                  Our website may use cookies and similar technologies to enhance your browsing experience. You can set your
                  browser to refuse cookies, though some features of the website may not function properly as a result.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">7. Third-Party Links</h2>
                <p>
                  Our website may contain links to third-party websites, including the official PureHeart International
                  website. We are not responsible for the privacy practices of these external sites and encourage you to
                  review their privacy policies.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">8. Children&apos;s Privacy</h2>
                <p>
                  Our website is not directed to children under the age of 13, and we do not knowingly collect personal
                  information from children. If you believe a child has provided us with personal information, please contact
                  us and we will delete it promptly.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">9. Your Rights</h2>
                <p>
                  You may request access to, correction of, or deletion of your personal information at any time by contacting
                  us using the details below. You may also unsubscribe from our communications at any time.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-4">10. Contact Us</h2>
                <p className="mb-3">
                  If you have any questions about this Privacy Policy or how we handle your personal information, please
                  reach out to us using any of the details below:
                </p>
                <ul className="list-disc pl-6 space-y-2 mb-3">
                  <li>
                    <strong>Email:</strong>{' '}
                    <a href="mailto:info@tgmci.org" className="text-primary-600 hover:text-primary-700 font-medium">info@tgmci.org</a>{' '}
                    or{' '}
                    <a href="mailto:donations@tgmci.org" className="text-primary-600 hover:text-primary-700 font-medium">donations@tgmci.org</a>
                  </li>
                  <li><strong>Head Office:</strong> Dansoman, Accra, Ghana</li>
                  <li><strong>Cameroon Branch:</strong> Mbanda Sic. 865, Edea, Cameroon</li>
                  <li><strong>Phone:</strong> +237 653 760 724 (Cameroon) or +1 623 800 9241 (USA)</li>
                </ul>
                <p>
                  You may also reach us through our <Link to="/contact" className="text-primary-600 hover:text-primary-700 font-medium">contact page</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}